import React, { useState, useCallback } from 'react';
import { Modal, Button, Input, Form } from '@crisellevaldez/speculo-ui';
import { AlertTriangle, Globe, Loader2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../lib/firebase';
import { useChangeDomain, validateDomain } from '../../../../hooks/client/tenant/useChangeDomain';

interface ChangeDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  onError: (error: Error) => void;
  currentDomain: string;
  organizationId: string;
}

export const ChangeDomainModal: React.FC<ChangeDomainModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  currentDomain,
  organizationId,
}) => {
  const [newDomain, setNewDomain] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  const { mutateAsync: changeDomain, isPending: isSubmitting } = useChangeDomain();

  const handleDomainChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewDomain(value);
    setDomainError(null); // Clear error when typing to allow user to correct
  }, []);

  const handleCheckDomain = async () => {
    if (!newDomain || newDomain === currentDomain) {
      return;
    }

    // Validate domain format first
    const validation = validateDomain(newDomain);
    if (!validation.isValid) {
      setDomainError(validation.error || 'Invalid domain format');
      return;
    }

    setIsCheckingDomain(true);
    setDomainError(null);

    try {
      // Check if domain already exists in domain-mappings
      const mappingRef = doc(db, 'domain-mappings', newDomain);
      const mappingDoc = await getDoc(mappingRef);

      if (mappingDoc.exists()) {
        const data = mappingDoc.data();
        // If it belongs to a different tenant, show error
        if (data.tenantId !== organizationId) {
          setDomainError(`Domain "${newDomain}" is already registered to another organization.`);
          setIsCheckingDomain(false);
          return;
        }
      }

      // Domain is available, show confirmation
      setShowConfirmation(true);
    } catch (error) {
      setDomainError('Failed to check domain availability. Please try again.');
    } finally {
      setIsCheckingDomain(false);
    }
  };

  const handleConfirmChange = async () => {
    try {
      // Use the hook to change domain
      // This will:
      // 1. Validate domain format
      // 2. Register domain in Firebase Auth and Hosting
      // 3. Only if register succeeds: Delete old domain mapping
      // 4. Only if register succeeds: Update tenant with new domain
      // 5. Only if register succeeds: Create new domain mapping
      await changeDomain({
        oldDomain: currentDomain,
        newDomain: newDomain,
        organizationId: organizationId,
      });

      // Wait for success callback to complete (shows toast), then close modal
      await onSuccess();
      handleClose();
    } catch (error) {
      onError(error instanceof Error ? error : new Error('Failed to change domain'));
    }
  };

  const handleClose = () => {
    setNewDomain('');
    setShowConfirmation(false);
    setDomainError(null);
    onClose();
  };

  // Determine if continue button should be disabled
  const isContinueDisabled =
    !newDomain || newDomain === currentDomain || isCheckingDomain || !!domainError;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Change Domain</Modal.Header>
      <Modal.Body>
        {!showConfirmation ? (
          <div className="space-y-6">
            {/* Current Domain Display */}
            <div className="bg-gray-50 dark:bg-white/5 p-4 rounded-lg border border-gray-200 dark:border-white/10">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Current Domain</p>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-foreground">{currentDomain}</span>
              </div>
            </div>

            {/* New Domain Input */}
            <Form.Control>
              <Form.Label>New Domain</Form.Label>
              <Input
                value={newDomain}
                onChange={handleDomainChange}
                placeholder="aisearch.newdomain.com"
                error={domainError || undefined}
              />
              <Form.HelperText>
                Enter the new domain for this website (e.g., aisearch.newdomain.com).
              </Form.HelperText>
            </Form.Control>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-white/10">
              <Button variant="outline" onClick={handleClose} disabled={isCheckingDomain}>
                Cancel
              </Button>
              <Button onClick={handleCheckDomain} disabled={isContinueDisabled}>
                {isCheckingDomain ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Checking...
                  </>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Confirmation View */}
            <div className="flex items-start gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-300">Confirm Domain Change</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                  You are about to change the domain from:
                </p>
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">From:</span>
                    <code className="bg-white dark:bg-white/10 px-2 py-0.5 rounded">{currentDomain}</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 dark:text-gray-400">To:</span>
                    <code className="bg-white dark:bg-white/10 px-2 py-0.5 rounded">{newDomain}</code>
                  </div>
                </div>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-3">
                  After this change, your website will be accessible at the new domain. Any links or
                  bookmarks using the old domain will stop working.
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-2">
                  <strong>Note:</strong> Please make sure to add the new DNS records. Propagation
                  may take up to 24 hours or more.
                </p>
              </div>
            </div>

            {/* Confirmation Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t dark:border-white/10">
              <Button variant="outline" onClick={() => setShowConfirmation(false)}>
                Back
              </Button>
              <Button onClick={handleConfirmChange} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Changing Domain...
                  </>
                ) : (
                  'Confirm Change'
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
